import { FC } from "react";
import QuickDetailCard from "./QuickDetailCard";
import { usePricingOptions } from "routes/data-routes/billing-data/pricing-options";
import { OverviewStatsSummary } from "utils/analytics/types";
import { cx, fmtNumber } from "utils";


interface AnalyticsQuickStatsProps {
  stats: OverviewStatsSummary | null;
  isLoading: boolean;
  error: string | null;
}

interface StatCardConfig {
  firstHeader: string;
  firstHeaderTooltip?: string;
  firstValue: (stats: OverviewStatsSummary) => string;
  secondHeader: string;
  secondHeaderTooltip?: string;
  secondValue: (stats: OverviewStatsSummary) => string;
  // Only shown to users with pay-as-you-go enabled
  requiresPayg?: boolean;
}

// Sub-second values read fine in ms; once we cross a second, "15.99 s" beats "15988 ms".
const formatResponseTime = (ms: number): string =>
  ms >= 1000 ? `${(ms / 1000).toFixed(2)}s` : `${Math.round(ms)}ms`;

const STAT_CARDS: StatCardConfig[] = [
  {
    firstHeader: "Requests sent",
    firstValue: (stats) => fmtNumber(stats.requestsSent),
    secondHeader: "Success rate",
    secondValue: (stats) => `${stats.successRate.toFixed(1)}%`,
  },
  {
    firstHeader: "Cancelled requests",
    firstHeaderTooltip:
      "Requests you stopped before they finished, for example by closing the connection. You don't get a response for these, but they're still charged.",
    firstValue: (stats) => fmtNumber(stats.cancelledRequests),
    secondHeader: "Credits charged",
    secondValue: (stats) => fmtNumber(stats.cancelledCredits),
  },
  {
    requiresPayg: true,
    firstHeader: "Pay-as-you-go requests",
    firstValue: (stats) => fmtNumber(stats.paygRequests),
    secondHeader: "Pay-as-you-go credits",
    secondValue: (stats) => fmtNumber(stats.paygCredits),
  },
  {
    firstHeader: "Avg. response time",
    firstValue: (stats) => formatResponseTime(stats.avgResponseTime),
    secondHeader: "Avg. concurrency",
    secondValue: (stats) => Math.round(stats.avgConcurrency).toString(),
  },
  {
    firstHeader: "Domains scraped",
    firstValue: (stats) => fmtNumber(stats.domainsScraped),
    secondHeader: "Credits per request",
    secondValue: (stats) => Math.round(stats.creditsPerRequest).toString(),
  },
  {
    firstHeader: "Parsers created",
    firstHeaderTooltip: "Includes all parsers created and edited",
    firstValue: (stats) => fmtNumber(stats.parsersCreated),
    secondHeader: "Credits consumed",
    secondValue: (stats) => fmtNumber(stats.parserCreditsConsumed),
  },
];

// Same padding as QuickDetailCard so the layout doesn't jump when the data arrives
const QuickDetailCardSkeleton: FC = () => (
  <div className="flex items-stretch border rounded-md overflow-hidden bg-white w-full">
    <div className="flex flex-1 divide-x min-w-0">
      <div className="flex flex-col justify-center p-3 md:p-2 lg:p-3 xl:p-4 flex-1 min-w-0 space-y-2">
        <div className="h-3 lg:h-4 bg-gray-200 rounded animate-pulse w-3/4" />
        <div className="h-4 lg:h-6 bg-gray-300 rounded animate-pulse w-1/2" />
      </div>
      <div className="flex flex-col justify-center p-3 md:p-2 lg:p-3 xl:p-4 flex-1 min-w-0 space-y-2">
        <div className="h-3 lg:h-4 bg-gray-200 rounded animate-pulse w-3/4" />
        <div className="h-4 lg:h-6 bg-gray-300 rounded animate-pulse w-1/2" />
      </div>
    </div>
  </div>
);

// As many cards per row as fit at 18rem or wider, so any card count wraps cleanly;
// fixed at 3 on desktop so 5 or 6 cards never leave one alone on the last row
const STAT_GRID_CLASS = "grid grid-cols-[repeat(auto-fit,minmax(18rem,1fr))] xl:grid-cols-3 gap-4";

const QuickStatsLoadingState: FC<{ cardCount: number }> = ({ cardCount }) => (
  <div className={STAT_GRID_CLASS}>
    {Array.from({ length: cardCount }).map((_, i) => (
      <QuickDetailCardSkeleton key={i} />
    ))}
  </div>
);

const QuickStatsEmptyState: FC = () => (
  <div className="p-4 text-center text-gray-500">No data available.</div>
);

const AnalyticsQuickStats: FC<AnalyticsQuickStatsProps> = ({
  stats,
  isLoading,
  error,
}) => {
  const { pricingOptions } = usePricingOptions();

  // Check if user has pay-as-you-go enabled
  const hasPayAsYouGo = pricingOptions?.["pay-as-you-go"]?.enabled ?? false;

  const visibleCards = STAT_CARDS.filter((card) => !card.requiresPayg || hasPayAsYouGo);

  if (isLoading) return <QuickStatsLoadingState cardCount={visibleCards.length} />;
  if (!error && !stats) return <QuickStatsEmptyState />;

  // On error the cards stay, dimmed, with "—" in place of the values
  const shownStats = error ? null : stats;

  return (
    <div className={cx(STAT_GRID_CLASS, error && "opacity-50")}>
      {visibleCards.map((card) => (
        <QuickDetailCard
          key={card.firstHeader}
          firstHeader={card.firstHeader}
          firstHeaderTooltip={card.firstHeaderTooltip}
          firstValue={shownStats ? card.firstValue(shownStats) : "—"}
          secondHeader={card.secondHeader}
          secondHeaderTooltip={card.secondHeaderTooltip}
          secondValue={shownStats ? card.secondValue(shownStats) : "—"}
        />
      ))}
    </div>
  );
};

export default AnalyticsQuickStats;
